import React, { useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import MobileEventItem from './MobileEventItem';
import { calculateEventPositions } from '../../../../utils/calendar/calendarHelpers';
import { getDayBounds, timeToPixels, pixelsToTime, snapToGrid } from '../../../../utils/calendar/timeCalculations';
import { CALENDAR_CONSTANTS } from '../../../../utils/calendar/constants';

const MobileDayTimeline = ({
                               selectedDay,
                               events,
                               workingHours,
                               isLockMode,
                               selectedLockSlots,
                               onEventClick,
                               onEventEdit,
                               onEventDelete,
                               onSlotClick,
                               onEditLock,
                               onEditTemporaryLock,
                               onDeleteTemporaryLock,
                               selectedTrainer
                           }) => {
    const timelineRef = useRef(null);
    const hourHeight = CALENDAR_CONSTANTS.MOBILE_HOUR_HEIGHT || 80;

    // Calculate day bounds and timeline info
    const { dayBounds, timeSlots, totalHeight } = useMemo(() => {
        const bounds = getDayBounds(selectedDay, workingHours);

        if (!bounds.isWorkingDay || !bounds.start || !bounds.end) {
            return { dayBounds: bounds, timeSlots: [], totalHeight: 0 };
        }

        // Generate time slots for display - FIXED: Only show hours where work actually happens
        const slots = [];
        const startHour = bounds.start.hour();
        const endHour = bounds.end.hour();

        // Only show hours where work actually happens
        // If work is 14:00-19:00, show 14:00, 15:00, 16:00, 17:00, 18:00 (stop before 19:00)
        for (let hour = startHour; hour < endHour; hour++) {
            slots.push({
                hour,
                label: `${hour.toString().padStart(2, '0')}:00`
            });
        }

        // If the end time has minutes (e.g., 19:30), then we should show the end hour too
        // because work happens during that hour
        if (bounds.end.minute() > 0) {
            slots.push({
                hour: endHour,
                label: `${endHour.toString().padStart(2, '0')}:00`
            });
        }

        const height = slots.length * hourHeight;

        return {
            dayBounds: bounds,
            timeSlots: slots,
            totalHeight: height
        };
    }, [selectedDay, workingHours, hourHeight]);

    // Filter and position events for this day
    const positionedEvents = useMemo(() => {
        if (!dayBounds.isWorkingDay) return [];

        const dayEvents = events.filter(event => {
            const eventStart = dayjs(event.startTime);

            // Special handling for pending events
            if (event.bookingCount > 0) {
                // Force strict date comparison
                const eventDateStr = eventStart.format('YYYY-MM-DD');
                const selectedDateStr = selectedDay.format('YYYY-MM-DD');

                console.log('Filtering pending event:', {
                    event,
                    eventDate: eventDateStr,
                    selectedDate: selectedDateStr,
                    matches: eventDateStr === selectedDateStr
                });

                return eventDateStr === selectedDateStr;
            }

            // Normal comparison for other events
            return eventStart.isSame(selectedDay, 'day');
        });

        return calculateEventPositions(dayEvents);
    }, [events, selectedDay, dayBounds]);

    // Handle empty space click
    const handleTimelineClick = (e) => {
        // Don't handle clicks on events
        if (e.target.closest('.mobile-event-wrapper')) return;

        const rect = timelineRef.current.getBoundingClientRect();
        const y = e.clientY - rect.top;

        // Calculate clicked time
        const clickedTime = pixelsToTime(
            y,
            dayBounds.start,
            hourHeight
        );

        // Snap to 15-minute intervals
        const snappedTime = snapToGrid(clickedTime, 15);

        // Ensure within working hours
        if (snappedTime.isSameOrAfter(dayBounds.start) && snappedTime.isBefore(dayBounds.end)) {
            onSlotClick(snappedTime, snappedTime.add(60, 'minute'));
        }
    };

    // Render no content states
    if (!selectedTrainer) {
        return (
            <div className="mobile-timeline-empty">
                <div className="mobile-no-working-hours">
                    <div className="mobile-no-events-icon">👤</div>
                    <div className="mobile-no-events-text">Wybierz trenera</div>
                </div>
            </div>
        );
    }

    if (!dayBounds.isWorkingDay) {
        return (
            <div className="mobile-timeline-empty">
                <div className="mobile-no-working-hours">
                    <div className="mobile-no-events-icon">📅</div>
                    <div className="mobile-no-events-text">Brak godzin pracy w tym dniu</div>
                </div>
            </div>
        );
    }

    return (
        <div className="mobile-timeline-container" style={{
            position: 'relative'
        }}>
            <div
                ref={timelineRef}
                className="mobile-timeline"
                style={{
                    position: 'relative',
                    height: `${totalHeight}px`,
                    cursor: isLockMode ? 'crosshair' : 'pointer'
                }}
                onClick={handleTimelineClick}
            >
                {/* Time labels */}
                <div className="mobile-time-grid" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '60px' }}>
                    {timeSlots.map((slot, index) => (
                        <div
                            key={slot.hour}
                            style={{
                                position: 'absolute',
                                top: `${index * hourHeight}px`,
                                left: 0,
                                right: 0,
                                height: `${hourHeight}px`,
                                paddingLeft: '8px',
                                paddingTop: '4px',
                                fontSize: '14px',
                                color: '#6b7280'
                            }}
                        >
                            {slot.label}
                        </div>
                    ))}
                </div>

                {/* Hour divider lines - full width */}
                <div className="mobile-hour-lines" style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    pointerEvents: 'none'
                }}>
                    {timeSlots.map((slot, index) => (
                        <div
                            key={`line-${slot.hour}`}
                            style={{
                                position: 'absolute',
                                top: `${index * hourHeight}px`,
                                left: 0,
                                right: 0,
                                height: '1px',
                                background: '#e5e7eb',
                                zIndex: 1
                            }}
                        />
                    ))}
                </div>

                {/* Events area */}
                <div className="mobile-events-area" style={{
                    position: 'absolute',
                    left: '60px',
                    right: '10px',
                    top: 0,
                    bottom: 0
                }}>
                    {/* Render positioned events */}
                    {positionedEvents.map((event, index) => {
                        const startTime = dayjs(event.startTime);
                        const endTime = dayjs(event.endTime);
                        const top = timeToPixels(startTime, dayBounds.start, hourHeight);
                        const height = timeToPixels(endTime, dayBounds.start, hourHeight) - top;

                        // Calculate width based on overlapping columns
                        const width = event.totalColumns > 1
                            ? `${(100 / event.totalColumns) - 2}%`
                            : '100%';
                        const left = event.totalColumns > 1
                            ? `${(event.column * (100 / event.totalColumns)) + 1}%`
                            : '0';

                        // Handle temporary lock edit/delete
                        let onEdit = onEventEdit;
                        let onDelete = onEventDelete;

                        if (event.isTemporary) {
                            const tempLockIndex = selectedLockSlots?.findIndex(lock =>
                                lock.startTime === event.startTime &&
                                lock.endTime === event.endTime
                            ) ?? -1;

                            if (tempLockIndex >= 0) {
                                onEdit = () => onEditTemporaryLock(tempLockIndex, event);
                                onDelete = () => onDeleteTemporaryLock(tempLockIndex);
                            }
                        }

                        return (
                            <div
                                key={event.id || `${event.startTime}-${index}`}
                                className="mobile-event-wrapper"
                                style={{
                                    position: 'absolute',
                                    top: `${top}px`,
                                    height: `${height}px`,
                                    left,
                                    width,
                                    padding: '2px'
                                }}
                            >
                                <div style={{ height: '100%' }}>
                                    <MobileEventItem
                                        event={event}
                                        onClick={onEventClick}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                        onEditLock={onEditLock}
                                        isLockMode={isLockMode}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Current time indicator (optional) */}
                {selectedDay.isSame(dayjs(), 'day') && (
                    <div
                        className="mobile-current-time"
                        style={{
                            position: 'absolute',
                            left: '50px',
                            right: 0,
                            height: '2px',
                            background: '#ef4444',
                            top: `${timeToPixels(dayjs(), dayBounds.start, hourHeight)}px`,
                            pointerEvents: 'none',
                            zIndex: 10
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            top: '-4px',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: '#ef4444'
                        }} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileDayTimeline;